import {
  ApplicationRef,
  ViewContainerRef,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
  Type,
  DOCUMENT,
  inject
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Observable } from 'rxjs';

interface PopupConfig {
  inputs?: { [key: string]: any };
  position?: 'top' | 'bottom' | 'center';
  margin?: boolean;
  fullWidth?: boolean;
  backdropDismiss?: boolean;
  transparentBackdrop?: boolean;
}

/**
 * Configuration interface for a SimpleAlert
 */
interface SimpleAlertConfig {
  /**
   * Title of the alert
   */
  title?: string;
  /**
   * Message of the alert
   */
  message?: string;
  /**
   * An array of button labels
   */
  buttons: Array<string>;
}

@Injectable({ providedIn: 'any' })
export class CorePopupService {
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private alertController = inject(AlertController);
  private htmlDocument = inject<HTMLDocument>(DOCUMENT);

  private backdropDismiss: boolean;
  private popupComponentRef: ComponentRef<any>;
  private backdropElement: HTMLElement;
  private containerElement: HTMLElement;
  public viewContainerRef: ViewContainerRef;

  set popupServiceRef(vcr: ViewContainerRef) {
    this.viewContainerRef = vcr;
  }

  public init(): void {
    if (this.htmlDocument.getElementById('bepatient-popup-backdrop')) {
      this.containerElement = this.htmlDocument.getElementById(
        'bepatient-popup-container'
      );
      this.backdropElement = this.htmlDocument.getElementById(
        'bepatient-popup-backdrop'
      );
    } else {
      this.backdropElement = this.htmlDocument.createElement('div');
      this.backdropElement.id = 'bepatient-popup-backdrop';
      this.htmlDocument
        .getElementsByTagName('ion-app')[0]
        .appendChild(this.backdropElement);
      this.containerElement = this.htmlDocument.createElement('div');
      this.containerElement.id = 'bepatient-popup-container';
      this.htmlDocument
        .getElementsByTagName('ion-app')[0]
        .appendChild(this.containerElement);
    }
  }

  public create<T>(popup: Type<T>, config: PopupConfig = {}): Promise<T> {
    this.init();
    return new Promise<T>(resolve => {
      this.dismiss();

      this.popupComponentRef = this.viewContainerRef?.createComponent(popup);

      if (config.inputs) {
        Object.keys(config.inputs).forEach(
          key => (this.popupComponentRef.instance[key] = config.inputs[key])
        );
      }

      config.position = config.position || 'center';

      const popupView = this.popupComponentRef.hostView;
      const popupElement = (popupView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;

      this.containerElement.appendChild(popupElement);

      const classes: string[] = ['display', config.position];
      if (config.margin) {
        classes.push('margin');
      }
      if (config.fullWidth) {
        classes.push('full-width');
      }

      if (config.transparentBackdrop) {
        this.backdropElement.style.background = 'rgba(0, 0, 0, 0)';
      }
      if ((this.backdropDismiss = config.backdropDismiss || false)) {
        this.backdropElement.addEventListener('click', () => this.dismiss());
      }

      this.backdropElement.className = 'display';
      this.containerElement.className = classes.join(' ');
      this.appRef.tick();
      resolve(this.popupComponentRef.instance);
    });
  }

  public dismiss(): void {
    if (this.popupComponentRef) {
      if (this.backdropDismiss) {
        this.backdropElement.removeEventListener('click', () => this.dismiss());
      }
      this.appRef.detachView(this.popupComponentRef.hostView);
      this.popupComponentRef.destroy();
      this.backdropElement.removeAttribute('style');
      this.backdropElement.removeAttribute('class');
      this.containerElement.removeAttribute('class');
    }
  }

  /**
   * Display a simple popup
   *
   * @param config Alert configuration, see [SimpleAlertConfig]{@link #SimpleAlertConfig}
   *
   * @returns An Observable<{ index?: number; alert?: HTMLIonAlertElement }> with the index of
   * the clicked button and a reference to the Alert
   */
  public simpleAlert(
    config: SimpleAlertConfig
  ): Observable<{ index?: number; alert?: HTMLIonAlertElement }> {
    return new Observable<{ index?: number; alert?: HTMLIonAlertElement }>(
      subscriber => {
        this.alertController
          .create({
            mode: 'ios',
            cssClass: 'bepatient_dynamic_global_alert',
            backdropDismiss: false,
            header: config.title,
            message: config.message,
            buttons: config.buttons.map((button, index) => ({
              text: button,
              handler: () => {
                subscriber.next({ index });
                subscriber.complete();
              }
            }))
          })
          .then(alert => {
            alert.present();
            subscriber.next({ alert });
          });
      }
    );
  }
}
